import { useCombobox } from 'downshift';
import { isEqual, isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useTestId, useUpdateEffect } from '../../../../../utils';
import { defaultFormatSelectedValue } from '../../BaseSelect.utils';
import { useCreatableOption } from '../useCreatableOption';
import { useDropdownMenuProps } from '../useDropdownMenuProps';
import { useInputProps } from '../useInputProps';
import { useOptions } from '../useOptions';
import { getOptionPropsHelper } from '../utils';
import { useStateReducer } from './useStateReducer';
export var useSearch = _ref => {
  var {
    ref,
    value,
    formatSelectedValue = defaultFormatSelectedValue,
    options: optionsList,
    filterOptions,
    shouldShowPresetOptions = false,
    creatableOption,
    emptyState,
    disableInputClick,
    isLoading,
    onClick,
    onChange,
    onInputChange,
    onKeyDown,
    onSearchTermReset,
    onClear,
    id
  } = _ref;
  var [query, setQuery] = useState();
  var {
    hasSections,
    options,
    filteredOptions,
    clearOptions
  } = useOptions({
    options: optionsList,
    filterOptions,
    query,
    shouldShowPresetOptions
  });
  var [selectedOption, setSelectedOption] = useState(null);
  useEffect(() => {
    // Update the selected option only if there's nothing selected, we have a value to set and the component is not loading options.
    if (selectedOption || isNil(value) || isLoading) return;
    var defaultSelectionOption = {
      label: value,
      value
    };
    var optionThatMatchesValue = options.find(option => isEqual(option.value, value)); // A default option to use in case we don't have any matching option to use.

    setSelectedOption(optionThatMatchesValue || defaultSelectionOption);
  }, [isLoading, options, selectedOption, value]);

  var handleChange = value => {
    onChange === null || onChange === void 0 ? void 0 : onChange({
      target: {
        value
      }
    });
  };

  var clearSelectedOption = () => {
    setSelectedOption(null);
    handleChange(null);
  };

  var handleInputChange = inputChangeValue => {
    onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(inputChangeValue);
    setQuery(inputChangeValue);
  };

  var {
    shouldShowCreatableOption,
    setShouldShowCreatableOption,
    newOption
  } = useCreatableOption({
    query,
    options,
    creatableOption
  });
  var stateReducer = useStateReducer({
    formatSelectedValue,
    hasFilteredOptions: Boolean(filteredOptions.length),
    hasEmptyState: Boolean(emptyState),
    hasCreatableOption: shouldShowCreatableOption,
    shouldOpenMenuOnClick: !value && Boolean(options.length),
    shouldShowPresetOptions,
    clearSelectedOption,
    setQuery,
    onInputChange: handleInputChange
  });
  var {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    setHighlightedIndex,
    inputValue,
    setInputValue,
    openMenu,
    closeMenu,
    selectItem
  } = useCombobox({
    defaultHighlightedIndex: 0,
    inputId: id,
    // Make sure the new creatable option is available for keyboard navigation.
    items: [...filteredOptions, ...(shouldShowCreatableOption && newOption ? [newOption] : [])],
    itemToString: option => option ? formatSelectedValue(option) : '',
    selectedItem: selectedOption,
    onSelectedItemChange: _ref2 => {
      var {
        selectedItem: newSelectedOption
      } = _ref2;
      setSelectedOption(newSelectedOption);

      if (newOption && newSelectedOption === newOption) {
        var _creatableOption$onCl;

        setShouldShowCreatableOption(false);
        creatableOption === null || creatableOption === void 0 ? void 0 : (_creatableOption$onCl = creatableOption.onClick) === null || _creatableOption$onCl === void 0 ? void 0 : _creatableOption$onCl.call(creatableOption, query !== null && query !== void 0 ? query : '');
      }

      if (newSelectedOption && newSelectedOption !== newOption) handleChange(newSelectedOption.value);
    },
    stateReducer
  }); // Clears the select if `value` is falsy.

  useUpdateEffect(() => {
    if (value) return;
    setInputValue('');
    selectItem(null);
    setQuery(null); // eslint-disable-next-line react-hooks/exhaustive-deps -- We can skip adding anything coming from `useCombobox` to the dependency list.
  }, [value]);
  useEffect(() => {
    if (query === null) {
      onSearchTermReset === null || onSearchTermReset === void 0 ? void 0 : onSearchTermReset();
    }
  }, [query, onSearchTermReset]);

  var clearSelect = () => {
    selectItem(null);
    setSelectedOption(null);
    handleChange(null);
    setQuery(null);
    clearOptions();
    onClear === null || onClear === void 0 ? void 0 : onClear();
  };

  var {
    dropdownMenuProps,
    anchorRef
  } = useDropdownMenuProps({
    ref,
    isOpen,
    shouldShowCreatableOption,
    getMenuProps,
    hasSections,
    options: filteredOptions,
    emptyState,
    closeMenu,
    resetHighlightedIndex: () => setHighlightedIndex(-1)
  });
  var inputProps = useInputProps({
    ref: anchorRef,
    getInputProps,
    onClick,
    onKeyDown,
    disableClick: disableInputClick,
    closeMenu,
    isLoading
  });
  return {
    dropdownMenuProps,
    inputProps,
    hasSections,
    openMenu,
    clearSelect,
    getOptionProps: getOptionPropsHelper({
      getItemProps,
      highlightedIndex,
      getTestId: useTestId('base-select')
    }),
    inputValue,
    selectedOption,
    filteredOptions
  };
};
//# sourceMappingURL=useSearch.js.map